import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import {
  FileSelector,
  type FileViewerFileType,
} from './_file-viewer-file-selector';
import { TimeWithTimeZone } from '@common/components';
import { Empty } from 'antd';
import { Colors } from '@common/tokens';
import clsx from 'clsx';
import { FilePreview } from './_file-viewer-file-preview';

type FileViewerSourceType = string | FileViewerFileType[];

export function FileViewer({
  children,
  className,
  source,
}: PropsWithChildren<{
  className?: string;
  source: FileViewerSourceType;
}>) {
  const sourceFiles = useMemo(() => handleSourceFiles(source), [source]);
  const hasAnySource = sourceFiles.some((v) => v?.url);
  const [selectedFile, setSelectedFile] = useState<
    FileViewerFileType | undefined
  >(sourceFiles[0]);

  useEffect(() => {
    setSelectedFile(sourceFiles[0]);
  }, [sourceFiles]);

  return (
    <div className={clsx([className, 'wk-file-viewer'])}>
      {hasAnySource ? (
        <div className="wk-file-viewer__content">
          {children && <div>{children}</div>}
          <div>
            <FileSelector
              sourceFiles={sourceFiles}
              selectedFile={selectedFile}
              onChange={(fileId: string) => {
                const file = sourceFiles.find(
                  (sourceFile) => sourceFile.id === fileId
                );
                setSelectedFile(file);
              }}
            />
            {selectedFile?.uploadedAt && (
              <div className="wk-file-viewer__uploaded-at">
                Uploaded at{' '}
                <TimeWithTimeZone
                  date={selectedFile?.uploadedAt}
                  variant="long_format"
                />
              </div>
            )}
          </div>
          {selectedFile?.url ? (
            <div className="wk-file-viewer__doc-overlay">
              {/*TODO: We should send the content type from all places that use the FileViewer - WORK-11823*/}
              <FilePreview
                fileName={selectedFile.name}
                assetUrl={selectedFile.url}
                contentType={
                  selectedFile?.contentType || 'forceBackwardsCompatibility'
                }
              />
            </div>
          ) : (
            <Empty data-testid="empty-state" description="No file found" />
          )}
        </div>
      ) : (
        <Empty data-testid="empty-state" />
      )}
      <style jsx>{`
        .wk-file-viewer,
        .wk-file-viewer__content,
        .wk-file-viewer__doc-overlay {
          height: 100%;
        }
        .wk-file-viewer__content {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .wk-file-viewer__doc-overlay :global(.wk-image-preview) {
          margin-bottom: 16px;
        }
        .wk-file-viewer__uploaded-at {
          color: ${Colors.midnightRegular};
          font-size: 12px;
          line-height: 16px;
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
}

function handleSourceFiles(source: FileViewerSourceType) {
  if (!source) return [];

  if (typeof source === 'string') {
    return [
      {
        id: source,
        url: source,
        name: getFileNameFromUrl(source),
      },
    ];
  }

  return source;
}

function getFileNameFromUrl(url: string) {
  const decoded = decodeURI(url);
  const chunks = decoded.split('/');
  const lastChunk = chunks[chunks.length - 1];
  return lastChunk.split('?')[0];
}
